import React, { useCallback, useEffect, useState } from 'react';
import theme from '../../theme';
import { truncateText } from '../../utils/helpers';
import SyntheticTransferModal from './SyntheticTransferModal';
import {
  addInstrumentToFolder,
  fetchScannerLibrary,
  promoteInstrumentToTrading,
  removeInstrumentFromFolder,
} from '../../services/api';

const chip = (active) => `rounded-lg px-2 py-1 text-[11px] font-bold ${
  active ? 'bg-olive-500 text-white' : 'bg-olive-50 text-olive-600 hover:bg-olive-100'
}`;

/**
 * Folder and trading membership for the instrument on screen.
 *
 * The same two moves the library offers, without leaving the panel: which folder
 * holds it, and which account trades it. Both are one click, because the pickers
 * they replace only ever asked one question — and the parent account, when the
 * server needs it.
 */
const InstrumentPlacement = ({ asset, accounts = [], onChanged, onPromoted }) => {
  const [library, setLibrary] = useState(null);
  const [pendingParent, setPendingParent] = useState(null);  // { folderId, accounts }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pendingTransfer, setPendingTransfer] = useState(null);

  const reload = useCallback(async () => {
    const data = await fetchScannerLibrary();
    if (data) setLibrary(data);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!asset || !library) return null;

  const key = `${asset.broker}|${asset.symbol}|${asset.sectype || ''}`;
  const entry = library.instruments.find(
    (i) => `${i.broker}|${i.symbol}|${i.sectype || ''}` === key,
  );
  const folderId = entry?.folder_id ?? null;
  // OKX is one account per user, and the library (like the panels) addresses it
  // by the username — the linked account's own name matches nothing here.
  const username = (library.monitor_account || '').replace('MONITOR:', '');
  const tradingAccounts = accounts
    .filter((a) => a.broker === asset.broker && a.broker !== 'virtual')
    .map((a) => (a.broker === 'okx' && username ? { ...a, account: username } : a));

  const after = async () => {
    await reload();
    onChanged?.();
  };

  const run = async (action) => {
    setBusy(true);
    setError(null);
    const result = await action();
    setBusy(false);
    if (result?.error) setError(String(result.error));
    return result;
  };

  const toFolder = async (id, parentAccount = null) => {
    const result = await run(() => addInstrumentToFolder(id, asset, parentAccount));
    if (result?.needsParent) {
      setPendingParent({ folderId: id, accounts: result.accounts });
      return;
    }
    setPendingParent(null);
    await after();
  };

  const outOfFolder = async () => {
    await run(() => removeInstrumentFromFolder(folderId, asset));
    await after();
  };

  const promote = async (instrument, next, account) => {
    const result = await run(() => promoteInstrumentToTrading(instrument, next));
    if (result?.needsTransfer) {
      setPendingTransfer({ id: result.syntheticId, next, account });
      return;
    }
    if (!result) setError('Could not update the trading accounts');
    else if (!result.error && next.includes(account)) onPromoted?.(instrument.broker, account);
    await after();
  };

  // The whole set travels on every click: an account already trading is being
  // switched off, one that is not is joining the others.
  const trading = entry?.trading_accounts || [];
  const toggleTrading = async (account) => {
    const next = trading.includes(account)
      ? trading.filter((a) => a !== account)
      : [...trading, account];
    await promote(asset, next, account);
  };

  return (
    <div className={`rounded-2xl ${theme.cardBg} ${theme.cardBorder} px-3 py-2 ${busy ? 'opacity-60' : ''}`}>
      {error && (
        <p className="mb-1 text-[11px] text-red-500">
          {error}
          <button className="ml-2 font-bold" onClick={() => setError(null)}>×</button>
        </p>
      )}

      {pendingParent ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`text-[11px] ${theme.textMuted}`}>Settings from:</span>
          {pendingParent.accounts.map((account) => (
            <button key={account} className={chip(false)} disabled={busy}
                    onClick={() => toFolder(pendingParent.folderId, account)}>
              {account}
            </button>
          ))}
          <button className={`text-[11px] ${theme.textMuted}`} onClick={() => setPendingParent(null)}>
            cancel
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-[11px] ${theme.textMuted}`}>📁</span>
            {library.folders.map((folder) => (
              <button
                key={folder.id}
                className={chip(folder.id === folderId)}
                disabled={busy}
                onClick={() => (folder.id === folderId ? outOfFolder() : toFolder(folder.id))}
                title={folder.id === folderId ? 'Back to the pool' : `Move to ${folder.name}`}
              >
                {truncateText(folder.name, 14)}
              </button>
            ))}
            {folderId === null && (
              <span className={`text-[11px] ${theme.textMuted}`}>not in a folder</span>
            )}
          </div>

          {tradingAccounts.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className={`text-[11px] ${theme.textMuted}`}>→ TRADE</span>
              {tradingAccounts.map((account) => (
                <button
                  key={account.id ?? account.account}
                  className={chip(trading.includes(account.account))}
                  disabled={busy}
                  onClick={() => toggleTrading(account.account)}
                  title={trading.includes(account.account)
                    ? `Stop trading on ${account.account}`
                    : `Trade on ${account.account}`}
                >
                  {account.account}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {pendingTransfer && (
        <SyntheticTransferModal
          syntheticId={pendingTransfer.id}
          onClose={() => setPendingTransfer(null)}
          onTransferred={(instrument) => promote(instrument, pendingTransfer.next, pendingTransfer.account)}
        />
      )}
    </div>
  );
};

export default InstrumentPlacement;
